import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import { getAcademicYears, getCurrentAcademicYear, getFeeCategories, getAllClassFeeStructures } from '../../services/feeService';
import FeeStructureEditor from './components/FeeStructureEditor';
import BulkFeeUploadModal from './components/BulkFeeUploadModal';

const CLASS_LIST = ['LKG', 'UKG', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

const FeeStructureManagement = () => {
  const navigate = useNavigate();
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState('');
  const [selectedClass, setSelectedClass] = useState('I');
  const [feeCategories, setFeeCategories] = useState([]);
  const [existingData, setExistingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  // Load academic years and fee categories on mount
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [years, cats, currentYear] = await Promise.all([
          getAcademicYears(),
          getFeeCategories(),
          getCurrentAcademicYear()
        ]);
        setAcademicYears(years || []);
        setFeeCategories(cats || []);
        if (currentYear?.id) {
          setSelectedYearId(currentYear?.id);
        } else if (years?.length > 0) {
          setSelectedYearId(years?.[0]?.id);
        }
      } catch (err) {
        setError('Failed to load data. Please refresh.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // Load existing fee structure when class or year changes
  useEffect(() => {
    if (!selectedYearId || !selectedClass) return;
    const load = async () => {
      setDataLoading(true);
      try {
        const data = await getAllClassFeeStructures(selectedYearId);
        const classData = data?.filter(d => d?.className === selectedClass || d?.class_name === selectedClass);
        setExistingData(classData || []);
      } catch (err) {
        setExistingData([]);
      } finally {
        setDataLoading(false);
      }
    };
    load();
  }, [selectedYearId, selectedClass, refreshKey]);

  const handleSaved = () => {
    setRefreshKey(k => k + 1);
  };

  const handleBulkUploaded = () => {
    setRefreshKey(k => k + 1);
  };

  const selectedYear = academicYears?.find(y => y?.id === selectedYearId);

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebarNavigation />
      <main className="flex-1 lg:ml-64 p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/admin-dashboard')}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
              >
                <Icon name="ArrowLeft" size={20} className="text-muted-foreground" />
              </button>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                Fee Structure Management
              </h1>
            </div>
            {!loading && !error && (
              <button
                onClick={() => setShowBulkUpload(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors flex-shrink-0"
              >
                <Icon name="Upload" size={16} />
                <span className="hidden sm:inline">Bulk Upload</span>
                <span className="sm:hidden">Upload</span>
              </button>
            )}
          </div>
          <p className="text-sm text-muted-foreground font-caption ml-10">
            Set fee amounts per class for each academic year and term
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Icon name="Loader2" size={32} className="animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="p-4 bg-error/10 border border-error/20 rounded-lg text-error text-sm">{error}</div>
        ) : (
          <>
            {/* Filters */}
            <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Academic Year */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Academic Year</label>
                  <select
                    value={selectedYearId}
                    onChange={e => setSelectedYearId(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {academicYears?.map(y => (
                      <option key={y?.id} value={y?.id}>
                        {y?.yearName || y?.year_name} {y?.isCurrent || y?.is_current ? '(Current)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Class */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Class</label>
                  <div className="flex flex-wrap gap-2">
                    {CLASS_LIST?.map(cls => (
                      <button
                        key={cls}
                        onClick={() => setSelectedClass(cls)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                          selectedClass === cls
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background text-foreground border-border hover:border-primary/50'
                        }`}
                      >
                        {cls}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Fee Structure Editor */}
            <div className="bg-card border border-border rounded-lg p-4 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="IndianRupee" size={20} className="text-primary" />
                <h2 className="text-lg font-heading font-semibold text-foreground">
                  Class {selectedClass} — {selectedYear?.yearName || selectedYear?.year_name || 'Select Year'}
                </h2>
                {dataLoading && <Icon name="Loader2" size={16} className="animate-spin text-muted-foreground ml-2" />}
              </div>

              {feeCategories?.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <Icon name="AlertCircle" size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No fee categories found. Please add fee categories first.</p>
                </div>
              ) : !selectedYearId ? (
                <div className="text-center py-10 text-muted-foreground">
                  <p className="text-sm">Please select an academic year to continue.</p>
                </div>
              ) : (
                <FeeStructureEditor
                  key={`${selectedClass}-${selectedYearId}-${refreshKey}`}
                  className={selectedClass}
                  academicYearId={selectedYearId}
                  feeCategories={feeCategories}
                  existingData={existingData}
                  onSaved={handleSaved}
                />
              )}
            </div>

            {/* Info note */}
            <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg flex items-start gap-2">
              <Icon name="Info" size={16} className="text-primary flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                Fee amounts set here will automatically appear on the student fee payment screen based on their class. 
                Students from <strong>Class {selectedClass}</strong> will see these exact amounts when they log in.
              </p>
            </div>
          </>
        )}

        {/* Bulk Upload Modal */}
        <BulkFeeUploadModal
          isOpen={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          academicYearId={selectedYearId}
          feeCategories={feeCategories}
          onUploaded={handleBulkUploaded}
        />
      </main>
    </div>
  );
};

export default FeeStructureManagement;
